import React from 'react';

import {
    calculateFactoredRange,
    getRangedValue, OWN_AMOUNT_STEP, PROPERTY_COST_MAX, PROPERTY_COST_MIN, PROPERTY_COST_STEP, TERM_MAX, TERM_MIN
} from '@search/vtbeco-frontend-core/view/common/components/MinimalBanksMortgage/MinimalBanksMortgageFields/constants';
import { formatNumber } from '@search/helpers/src/formatNumber';
import { UseBankMortgageFormProps } from '../BanksMortgageOffers/BanksMortgageOffers';

export const useBanksMortgageOffersForm = ({
    onChange,
    isBigLimit = false,
    ownAmountFromPropertyCost = true,
    ...initialState
}: UseBankMortgageFormProps & {
    ownAmountFromPropertyCost?: boolean;
    isBigLimit?: boolean;
}) => {
    const [ formState, setFormState ] = React.useState(initialState);

    const calculatedFormValues = React.useMemo(() => {
        const [ ownAmountMin, ownAmountMax ] = calculateFactoredRange(formState.propertyCost);
        const percent = getRangedValue(Math.round((formState.ownAmount / formState.propertyCost) * 100), 0, 100);

        return {
            propertyCost: {
                min: PROPERTY_COST_MIN,
                max: PROPERTY_COST_MAX,
                step: PROPERTY_COST_STEP
            },
            ownAmount: {
                min: ownAmountMin,
                max: ownAmountMax,
                step: OWN_AMOUNT_STEP,
                percent: `${percent ? `${percent}%` : ''}`,
                labelFrom: `от ${formatNumber(ownAmountMin)} ₽`,
                labelTo: `до ${formatNumber(ownAmountMax)} ₽`
            },
            term: {
                min: TERM_MIN,
                max: TERM_MAX
            }
        };
    }, [ formState.ownAmount, formState.propertyCost ]);

    const handleFieldChange = React.useCallback((
        name: keyof typeof formState,
        value: number | string
    ) => {
        setFormState(prevState => {
            let newState = prevState;

            let ownAmount = prevState.ownAmount;

            if (typeof value === 'number') {
                //@ts-ignore
                value = value ? Number(value) : (Number(calculatedFormValues[name]?.min) ?? 0);
                //@ts-ignore
                if (Number.isNaN(value)) value = (Number(calculatedFormValues[name]?.min) ?? 0);
            }

            if (name === 'propertyCost' && ownAmountFromPropertyCost) {
                const [ ownAmountMin, ownAmountMax ] = calculateFactoredRange(value as number);

                ownAmount = getRangedValue(prevState.ownAmount, ownAmountMin, ownAmountMax);
            }

            if (name === 'programCode') {
                switch (value) {
                    case 'STATE_PROGRAM_2020':
                        const p = newState.propertyCost - newState.ownAmount;
                        const cp = newState.propertyCost - 6000000;

                        if (p > 6000000) {
                            newState = {
                                ...newState,
                                ownAmount: cp
                            };
                        }
                        break;
                    case 'FAMILY':
                        const p1 = newState.propertyCost - newState.ownAmount;
                        const t = isBigLimit ? 12000000 : 6000000;
                        const cp1 = newState.propertyCost - t;

                        if (p1 > t) {
                            newState = {
                                ...newState,
                                ownAmount: cp1
                            };
                        }
                        break;
                    case 'IT_MORTGAGE':
                        const p2 = newState.propertyCost - newState.ownAmount;
                        const t1 = isBigLimit ? 18000000 : 9000000;
                        const cp2 = newState.propertyCost - t1;

                        if (p2 > t1) {
                            newState = {
                                ...newState,
                                ownAmount: cp2
                            };
                        }
                        break;
                    default:
                        break;
                }
            }

            if (
                prevState[name] !== value ||
                ownAmount !== prevState.ownAmount
            ) {
                newState = {
                    ...newState,
                    ownAmount,
                    [name]: value
                };
            }

            if (onChange && newState !== prevState) onChange(newState);

            return newState;
        });
    }, [ calculatedFormValues, onChange, ownAmountFromPropertyCost ]);

    return {
        formState,
        formErrors: {
            propertyCost: formState.propertyCost < calculatedFormValues.propertyCost.min ||
                formState.propertyCost > calculatedFormValues.propertyCost.max,
            ownAmount: formState.ownAmount < calculatedFormValues.ownAmount.min ||
                formState.ownAmount > calculatedFormValues.ownAmount.max
        },
        handleFieldChange,
        calculatedFormValues
    };
};
